import { useEffect, useRef, useState } from "react";
import { policyDocumentDownloadUrl, policyDocumentsClient } from "../apiClient/policyDocumentsClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import { POLICY_DIVISIONS, type PolicyDivision, type PolicyDocument } from "../types/policyDocument.types";

const MAX_DOCUMENT_MB = 25;

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function DocumentRow({ doc, onChanged }: { doc: PolicyDocument; onChanged: () => void }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingDivision, setSavingDivision] = useState(false);

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await policyDocumentsClient.deleteDocument(doc.id);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete this.");
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  }

  async function handleDivisionChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const division = e.target.value as PolicyDivision;
    setSavingDivision(true);
    setError(null);
    try {
      await policyDocumentsClient.updateDivision(doc.id, division);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't change the division.");
    } finally {
      setSavingDivision(false);
    }
  }

  return (
    <li className="policy-doc-row">
      <span className="policy-doc-info">
        <a className="policy-doc-name" href={policyDocumentDownloadUrl(doc.id)} target="_blank" rel="noopener">
          {doc.fileName}
        </a>
        <span className="policy-doc-meta">
          {formatFileSize(doc.fileSizeBytes)} · Uploaded {formatDate(doc.uploadedAt)}
        </span>
        <select
          className="policy-doc-division-select"
          value={doc.division ?? ""}
          onChange={handleDivisionChange}
          disabled={savingDivision}
          aria-label={`Division for ${doc.fileName}`}
        >
          {!doc.division && <option value="">Uncategorized</option>}
          {POLICY_DIVISIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </span>

      {confirmingDelete ? (
        <div className="cancel-confirm">
          <span>Delete?</span>
          <button type="button" className="btn btn-ghost" onClick={() => setConfirmingDelete(false)} disabled={submitting}>
            No
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={submitting}>
            {submitting ? "Deleting…" : "Yes"}
          </button>
        </div>
      ) : (
        <button type="button" className="btn btn-danger-ghost" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
          Delete
        </button>
      )}
      {error && <span className="field-error">{error}</span>}
    </li>
  );
}

export function PoliciesAdminPage() {
  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [division, setDivision] = useState<PolicyDivision>(POLICY_DIVISIONS[0]);

  function refresh() {
    setIsLoading(true);
    setError(null);
    policyDocumentsClient
      .getDocuments()
      .then(setDocuments)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load documents."))
      .finally(() => setIsLoading(false));
  }

  useEffect(refresh, []);

  async function handleFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.type !== "application/pdf") {
      setUploadError("Only PDF files are supported.");
      return;
    }
    if (file.size > MAX_DOCUMENT_MB * 1024 * 1024) {
      setUploadError(`File is too large — the limit is ${MAX_DOCUMENT_MB}MB.`);
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      await policyDocumentsClient.uploadDocument(file, division);
      refresh();
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : "Couldn't upload this document.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Manage policy documents</h1>
          <p className="page-sub">Add or remove the PDFs shown on the Policies and Procedures page.</p>
        </div>
      </header>

      <div className="form-card">
        <h2>Upload a document</h2>
        <div className="form">
          <div className="form-field">
            <label htmlFor="policy-division">Division</label>
            <select id="policy-division" value={division} onChange={(e) => setDivision(e.target.value as PolicyDivision)} disabled={uploading}>
              {POLICY_DIVISIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <span className="field-hint">Which section of the Policies page this document appears under.</span>
          </div>
          <input ref={fileInputRef} type="file" accept="application/pdf" hidden onChange={handleFileChosen} />
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? "Uploading…" : "Choose PDF"}
            </button>
          </div>
          {uploadError && <div className="form-banner form-banner--error">{uploadError}</div>}
        </div>
      </div>

      <h2 className="admin-section-heading">Uploaded documents</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : documents.length === 0 ? (
        <div className="state-panel">
          <p>No documents yet — upload the first one above.</p>
        </div>
      ) : (
        <ul className="policy-doc-list">
          {documents.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} onChanged={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}
